import mixpanel from 'mixpanel-browser';
import type { MixpanelClient, MixpanelClientOptions } from './mixpanel';

export async function createMixpanelClient(
  token: string,
  opts: MixpanelClientOptions = {},
): Promise<MixpanelClient> {
  mixpanel.init(token, {
    debug: Boolean(opts.debug),
    track_pageview: false,
    persistence: 'localStorage',
    api_host: opts.serverURL ?? 'https://api.mixpanel.com',
  });

  mixpanel.register({ platform: 'web' });

  return {
    track: (event, properties) => {
      mixpanel.track(event, properties);
    },
    identify: (distinctId, userProperties) => {
      mixpanel.identify(distinctId);
      if (userProperties && Object.keys(userProperties).length > 0) {
        mixpanel.people.set(userProperties);
      }
    },
    // This project runs Mixpanel's SIMPLIFIED ID Merge API: the server links the
    // pre-login session to the account by sending $device_id and $user_id on one
    // event, so this must return exactly the value Mixpanel stores as $device_id.
    //
    // The stored property is read first because it stays correct after
    // identify(), whereas get_distinct_id() then returns the user id. The
    // fallback strips the "$device:" prefix Mixpanel puts on the pre-login
    // distinct id, which is display form rather than the merge key.
    getDeviceId: () => {
      try {
        const stored = mixpanel.get_property('$device_id');
        if (typeof stored === 'string' && stored.length > 0) return stored;

        const distinctId = mixpanel.get_distinct_id();
        return typeof distinctId === 'string' && distinctId.length > 0
          ? distinctId.replace(/^\$device:/, '')
          : undefined;
      } catch {
        return undefined;
      }
    },
    reset: () => mixpanel.reset(),
    register: (properties) => mixpanel.register(properties),
    setGroup: (groupType, groupKey, groupProperties) => {
      mixpanel.set_group(groupType, groupKey);
      if (groupProperties && Object.keys(groupProperties).length > 0) {
        mixpanel.get_group(groupType, groupKey).set(groupProperties);
      }
    },
    screen: (name, properties) => {
      mixpanel.track('Screen Viewed', { screen: name, ...(properties ?? {}) });
    },
    optInTracking: () => mixpanel.opt_in_tracking(),
    optOutTracking: () => mixpanel.opt_out_tracking(),
  };
}
